// File Metadata:
// Filename: ReceptionsDataGrid.js
// Version: 1.00
// Created: 2022-10-09 21:54:51
// Last Modified: 2025-04-01 17:27:27
// Authors: [Your Name]
// Description: A brief summary of what this file does

import './datatable.scss'
import { DataGrid, GridToolbar } from '@mui/x-data-grid'
import { Link, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import { useDeleteReceptionMutation, useGetReceptionsQuery } from '../api/receptionsApiSlice'
import useAuth from '../../hooks/useAuth'
import { tableBodyClasses } from '@mui/material'
import { format } from 'date-fns'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { Box, FormControl, InputLabel, Select, MenuItem, useMediaQuery, useTheme, Button, Typography, Paper, Stack, TextField, Chip, IconButton, Collapse, InputAdornment } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import SearchIcon from '@mui/icons-material/Search';

const ReceptionsDataGrid = () => {
	const theme = useTheme()
	const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
	const { username, isVoorman, isAdmin } = useAuth()
	const navigate = useNavigate()
	const {
		data: receptions,
		isLoading,
		isSuccess,
		isError,
		error
	} = useGetReceptionsQuery('receptionsList', {
		pollingInterval: 15000,
		refetchOnFocus: true,
		refetchOnMountOrArgChange: true
	})
	const [deleteReception, { isSuccess: isDelSuccess, isError: isDelError, error: delerror }] =
		useDeleteReceptionMutation()

	const handleDelete = async id => {
		await deleteReception({ id })
	}

	const [qualityFilter, setQualityFilter] = useState('ALL')
	const [putawayFilter, setPutawayFilter] = useState('PENDING')
	const [searchText, setSearchText] = useState('')
	const [filtersExpanded, setFiltersExpanded] = useState(false)
	
	// Add initial sort model state
    const [sortModel, setSortModel] = useState([
        {
            field: 'deliveryDate',
            sort: 'desc',
        },
    ]);

	const userColumns = [
		{
			field: 'deliveryDate',
			headerName: 'Delivery Date',
			//headerClassName: 'fieldTitle',
			flex: 2,
			renderCell: params => {
				return format(new Date(params.row.deliveryDate), 'dd-MM-yyyy')
			}
		},
		{
			field: 'unloadLocation',
			headerName: 'Unload Location',
			flex: 2
		},
		{
			field: 'unloadedBy',
			headerName: 'Unloaded By',
			flex: 2
		},

		{
			field: 'vendorNumber',
			headerName: 'Vendor Number',
			flex: 2
		},
		{
			field: 'vendorName',
			headerName: 'Vendor Name',
			flex: 2
		},
		{
			field: 'tourNumber',
			headerName: 'TU',
			flex: 2
		},
		{
			field: 'goodsReceipt',
			headerName: 'GR',
			flex: 1,
			renderCell: params => {
				return params.row?.goodsReceipt ? <FontAwesomeIcon icon={faCheck} /> : ''
			}
		},
		{
			field: 'putawayStatus',
			headerName: 'Putaway Status',
			flex: 1,
			renderCell: params => {
				return params.row?.putawayStatus ? <FontAwesomeIcon icon={faCheck} /> : ''
			}
		},
		{
			field: 'qualityStatus',
			headerName: 'Quality Status',
			width: 140,
			flex: 0,
			renderCell: (params) => {
				const status = params.row.qualityStatus || 'PENDING';  // Default to PENDING if no status
				const statusColors = {
					'PENDING': '#9e9e9e',  // Grey for pending
					'PENDING_APPROVAL': '#ffa726',
					'APPROVED': '#66bb6a',
					'REJECTED': '#f44336',
					'NEEDS_REVIEW': '#29b6f6',
					'CONDITIONALLY_APPROVED': '#8d6e63'
				};
				
				return (
					<Box
						sx={{
							backgroundColor: statusColors[status] || '#grey',
							padding: '4px 8px',
							borderRadius: '4px',
							color: 'white',
							whiteSpace: 'nowrap'
						}}
					>
						{status?.replace('_', ' ')}
					</Box>
				);
			}
		},
		{
			field: 'notes',
			headerName: 'Notes',
			flex: 6
		}
	]

	const mobileColumns = [
		{
			field: 'reception',
			headerName: '#',
			width: 70,
		},
		{
			field: 'vendorName',
			headerName: 'Vendor',
			width: 130
		},
		{
			field: 'qualityStatus',
			headerName: 'QC',
			width: 90,
			renderCell: (params) => (
				<Chip 
					size="small"
					label={params.value}
					color={params.value === 'APPROVED' ? 'success' : 
						   params.value === 'REJECTED' ? 'error' : 'default'}
				/>
			)
		},
		{
			field: 'putawayStatus',
			headerName: 'PA',
			width: 70,
			renderCell: (params) => (
				<Chip 
					size="small"
					label={params.value ? 'Done' : 'Open'}
					color={params.value ? 'success' : 'warning'}
				/>
			)
		}
	]

	let content

	if (isLoading) content = <p>Loading...</p>

	if (isError) {
		content = <p className='errmsg'>{error?.data?.message}</p>
	}

	if (isSuccess) {
		console.log(receptions)
		const { ids, entities } = receptions
		let filteredIds = [...ids]

		// Apply quality status filter
		if (qualityFilter !== 'ALL') {
			filteredIds = filteredIds.filter(id => 
				entities[id].qualityStatus === qualityFilter
			)
		}

		// Add putaway filter
        if (putawayFilter !== 'ALL') {
            filteredIds = filteredIds.filter(id => 
                putawayFilter === 'COMPLETED' ? entities[id].putawayStatus : !entities[id].putawayStatus
            )
        }

		const tableContent = filteredIds.map(id => entities[id])

		const desktopFilters = (
			<Box sx={{ 
				display: { xs: 'none', md: 'flex' }, 
				gap: 2,
				alignItems: 'center',
				mb: 2
			}}>
				<FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
					<InputLabel>Quality Status</InputLabel>
					<Select
						value={qualityFilter}
						label="Quality Status"
						onChange={(e) => setQualityFilter(e.target.value)}
					>
						<MenuItem value="ALL">All Status</MenuItem>
						<MenuItem value="PENDING_APPROVAL">Pending Approval</MenuItem>
						<MenuItem value="APPROVED">Approved</MenuItem>
						<MenuItem value="REJECTED">Rejected</MenuItem>
						<MenuItem value="NEEDS_REVIEW">Needs Review</MenuItem>
					</Select>
				</FormControl>

				<FormControl sx={{ minWidth: { xs: '100%', sm: 200 } }}>
					<InputLabel>Putaway Status</InputLabel>
					<Select
						value={putawayFilter}
						label="Putaway Status"
						onChange={(e) => setPutawayFilter(e.target.value)}
					>
						<MenuItem value="ALL">All</MenuItem>
						<MenuItem value="COMPLETED">Completed</MenuItem>
						<MenuItem value="PENDING">Pending</MenuItem>
					</Select>
				</FormControl>

				<TextField
					label="Search"
					variant="outlined"
					value={searchText}
					onChange={(e) => setSearchText(e.target.value)}
					sx={{ minWidth: { xs: '100%', sm: 200 } }}
				/>
				<Button
					variant="contained"
					color="primary"
					component={Link}
					to="/dash/receptions/new"
					sx={{ 
						height: '56px',  // Match Select height
						whiteSpace: 'nowrap'
					}}
				>
					Add New
				</Button>
			</Box>
		)

		const mobileFilters = (
			<Box sx={{ 
				display: { xs: 'flex', md: 'none' },
				flexDirection: 'column',
				gap: 2,
				mb: 2
			}}>
				<Paper sx={{ width: '100%' }}>
					<Stack 
						direction="row" 
						justifyContent="space-between" 
						alignItems="center"
						onClick={() => setFiltersExpanded(!filtersExpanded)}
						sx={{ cursor: 'pointer', p: 2 }}
					>
						<Typography>Filters</Typography>
						<IconButton>
							{filtersExpanded ? <KeyboardArrowUpIcon /> : <ExpandMoreIcon />}
						</IconButton>
					</Stack>

					<Collapse in={filtersExpanded}>
						<Box sx={{ p: 2 }}>
							<Stack spacing={2}>
								<FormControl fullWidth>
									<InputLabel>Quality Status</InputLabel>
									<Select
										value={qualityFilter}
										label="Quality Status"
										onChange={(e) => setQualityFilter(e.target.value)}
									>
										<MenuItem value="ALL">All Status</MenuItem>
										<MenuItem value="PENDING_APPROVAL">Pending</MenuItem>
										<MenuItem value="APPROVED">Approved</MenuItem>
										<MenuItem value="REJECTED">Rejected</MenuItem>
									</Select>
								</FormControl>

								<FormControl fullWidth>
									<InputLabel>Putaway Status</InputLabel>
									<Select
										value={putawayFilter}
										label="Putaway Status"
										onChange={(e) => setPutawayFilter(e.target.value)}
									>
										<MenuItem value="ALL">All</MenuItem>
										<MenuItem value="COMPLETED">Completed</MenuItem>
										<MenuItem value="PENDING">Pending</MenuItem>
									</Select>
								</FormControl>

								<TextField
									fullWidth
									placeholder="Search..."
									variant="outlined"
									value={searchText}
									onChange={(e) => setSearchText(e.target.value)}
									InputProps={{
										startAdornment: (
											<InputAdornment position="start">
												<SearchIcon />
											</InputAdornment>
										),
									}}
								/>
							</Stack>
						</Box>
					</Collapse>
				</Paper>

				<Button
					variant="contained"
					color="primary"
					component={Link}
					to="/dash/receptions/new"
					sx={{ 
						height: '56px',  // Match Select height
						whiteSpace: 'nowrap'
					}}
				>
					Add New
				</Button>
			</Box>
		)

		const getFilteredData = (data) => {
			return data.filter(row => {
				const matchesQuality = qualityFilter === 'ALL' ? true : row.qualityStatus === qualityFilter;
				const matchesPutaway = putawayFilter === 'ALL' ? true : 
					putawayFilter === 'COMPLETED' ? row.putawayStatus : !row.putawayStatus;
				const matchesSearch = searchText === '' ? true :
					Object.values(row).some(value => 
						String(value).toLowerCase().includes(searchText.toLowerCase())
					);
				return matchesQuality && matchesPutaway && matchesSearch;
			});
		};

		content = (
			<Box 
				sx={{ 
					height: 600,
					width: '100%',
					p: 0,
					'& .MuiDataGrid-root': { border: 'none' }
				}}
			>
				<Typography variant="h6" sx={{ 
					color: 'gray',
					order: isMobile ? 0 : 1  // Title first on mobile, after filters on desktop
				}}>
					Receptions
				</Typography>
				{desktopFilters}
				{mobileFilters}
				
					<Box sx={{ 
                    width: '100%',
                    height: {
                        xs: 'calc(100vh - 200px)', // Mobile height
                        sm: 600 // Desktop height
                    },
                    '& .MuiDataGrid-root': {
                        border: 'none',
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f0f0f0'
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: 'background.paper',
                            borderBottom: '2px solid #f0f0f0'
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            backgroundColor: 'background.paper'
                        }
                    }
                }}>
					<DataGrid
						className='datagrid'
						rows={getFilteredData(tableContent)}
						columns={isMobile ? mobileColumns : userColumns}
						rowsPerPageOptions={isMobile ? [10, 25] : [25, 50, 100]}
						getRowId={row => row.id}
						components={{
							Toolbar: isMobile ? undefined : GridToolbar
						}}
						density={isMobile ? "compact" : "standard"}
						disableColumnFilter={isMobile}
						disableColumnMenu={isMobile}
						onRowClick={(params) => navigate(`/dash/receptions/${params.id}`)}
						sortModel={sortModel}
                        onSortModelChange={(model) => setSortModel(model)}
                        initialState={{
                            sorting: {
                                sortModel: [{
                                    field: 'deliveryDate',
                                    sort: 'desc',
                                }],
                            },
                        }}
					/>
				</Box>
			</Box>
		)
	}
	return content
}

export default ReceptionsDataGrid
